import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "endpoint",
    "apiVersion",
    "model",
    "apiKey",
    "endpointHint",
    "modelLabel",
    "providerSelect",
    // Dedicated embeddings connection (Grok/Claude chat cannot embed).
    "dedicatedEmbeddings",
    // Azure embeddings deployment name — shared by both semantic-search modes.
    "embeddingsDeployment",
  ]
  // Provider last saved on the account (may differ from the select after a
  // failed change). Used to decide whether the stored key still applies.
  static values = { savedProvider: String }

  // Providers that use the vendor's default API host — a leftover custom
  // endpoint from Grok/Azure must not be submitted with them.
  static ENDPOINTLESS_PROVIDERS = ["openai", "claude"]

  // Chat providers that can also run embeddings, so semantic search reuses the
  // chat credentials instead of a separate embeddings connection.
  static EMBEDDING_CHAT_PROVIDERS = ["openai", "azure_openai"]

  connect() {
    // Prefer the server-provided saved provider so a rejected Claude save still
    // opens the key field (select shows Claude, key still belongs to Grok).
    this.savedProvider = (this.hasSavedProviderValue && this.savedProviderValue)
      ? this.savedProviderValue.toLowerCase()
      : this.currentProvider()
    this.toggleFields()
  }

  currentProvider() {
    const checkedProvider = document.querySelector('input[type="radio"][name*="[ai_provider]"]:checked')
    const providerSelect = this.hasProviderSelectTarget
      ? this.providerSelectTarget
      : document.querySelector('select[name*="[ai_provider]"]')
    const rawProvider = checkedProvider ? checkedProvider.value : (providerSelect ? providerSelect.value : "")
    return rawProvider.toLowerCase()
  }

  toggleFields() {
    const currentProvider = this.currentProvider()

    // Hide all provider-specific hints first
    if (this.hasProviderHintTarget) {
      this.providerHintTargets.forEach(hint => {
        hint.style.display = 'none'
      })
    }

    // Show the hint for the current provider
    const currentHint = document.querySelector(`[data-provider-hint="${currentProvider}"]`)
    if (currentHint) {
      currentHint.style.display = 'block'
    }

    // Handle API key field visibility and required status
    if (this.hasApiKeyTarget) {
      const apiKeyGroup = this.apiKeyTarget.closest('.form-group')
      const apiKeyLabel = apiKeyGroup.querySelector('label')

      if (currentProvider === 'ollama') {
        apiKeyGroup.style.display = 'none'
        if (apiKeyLabel) apiKeyLabel.classList.remove('required')
      } else {
        apiKeyGroup.style.display = 'block'
        if (apiKeyLabel) apiKeyLabel.classList.add('required')
      }
    }

    // Handle endpoint field visibility and hint
    if (this.hasEndpointTarget) {
      const endpointGroup = this.endpointTarget.closest('.form-group')
      const endpointLabel = endpointGroup.querySelector('label')
      const endpointHint = this.hasEndpointHintTarget ? this.endpointHintTarget : endpointGroup.querySelector('.hint')

      if (this.constructor.ENDPOINTLESS_PROVIDERS.includes(currentProvider)) {
        endpointGroup.style.display = 'none'
        // Drop a prior Grok/Azure URL so it isn't quietly re-saved.
        this.endpointTarget.value = ""
        if (endpointLabel) endpointLabel.classList.remove('required')
      } else {
        endpointGroup.style.display = 'block'
        if (endpointLabel) endpointLabel.classList.add('required')
        if (endpointHint) endpointHint.textContent = ""
      }
    }

    // Handle API version field visibility
    if (this.hasApiVersionTarget) {
      const apiVersionGroup = this.apiVersionTarget.closest('.form-group')
      const apiVersionLabel = apiVersionGroup.querySelector('label')

      if (currentProvider === 'azure_openai') {
        apiVersionGroup.style.display = 'block'
        if (apiVersionLabel) apiVersionLabel.classList.add('required')
      } else {
        apiVersionGroup.style.display = 'none'
        if (apiVersionLabel) apiVersionLabel.classList.remove('required')
      }
    }

    // Handle model field visibility. Ollama is the only provider that still
    // names its model here: catalog providers pick one per message in the
    // composer, and Azure's deployment is part of its endpoint URL.
    if (this.hasModelTarget) {
      const modelField = this.modelTarget
      const modelGroup = modelField.closest('.form-group')
      const modelLabel = this.hasModelLabelTarget ? this.modelLabelTarget : modelGroup.querySelector('label')
      const modelHint = modelGroup.querySelector('.hint')

      if (currentProvider === 'ollama') {
        modelGroup.style.display = 'block'
        if (modelLabel) modelLabel.classList.add('required')
        modelField.placeholder = "llama3.1"
        if (modelHint) modelHint.textContent = "Required. Enter the name of your Ollama model (e.g., llama3.1, llama3.2, etc.)."
      } else {
        modelGroup.style.display = 'none'
        if (modelLabel) modelLabel.classList.remove('required')
      }
    }

    this.syncSemanticSearchFields(currentProvider)
    this.syncSecretField(currentProvider)
  }

  // Swap the Semantic search options to match the selected chat provider before
  // Save. Hidden mode fieldsets are disabled so their controls are not submitted
  // and cannot trip `required-fields` / server validation for the previous provider.
  syncSemanticSearchFields(chatProvider) {
    const canEmbed = this.constructor.EMBEDDING_CHAT_PROVIDERS.includes(chatProvider)

    if (this.hasDedicatedEmbeddingsTarget) {
      const showDedicated = !canEmbed
      this.dedicatedEmbeddingsTarget.style.display = showDedicated ? "" : "none"
      this.dedicatedEmbeddingsTarget.disabled = !showDedicated

      if (showDedicated) {
        const embeddingsConfig = this.application.getControllerForElementAndIdentifier(
          this.dedicatedEmbeddingsTarget,
          "embeddings-config",
        )
        embeddingsConfig?.toggleFields()
      }
    }

    this.syncEmbeddingsDeployment(canEmbed, chatProvider)
  }

  syncEmbeddingsDeployment(canEmbed, chatProvider) {
    if (!this.hasEmbeddingsDeploymentTarget) return

    const group = this.embeddingsDeploymentTarget
    const show = canEmbed
      ? chatProvider === "azure_openai"
      : this.dedicatedEmbeddingsProvider() === "azure_openai"

    group.style.display = show ? "" : "none"
    group.disabled = !show
    group.querySelector("label")?.classList.toggle("required", show)
  }

  dedicatedEmbeddingsProvider() {
    if (!this.hasDedicatedEmbeddingsTarget) return "openai"

    const select = this.dedicatedEmbeddingsTarget.querySelector('select[name*="[ai_embeddings_provider]"]')
    return (select?.value || "openai").toLowerCase()
  }

  // Picking a different provider invalidates the stored key: open the secret
  // field so a new key must be entered before the form can be saved.
  // Switching back to the saved provider restores the masked state.
  syncSecretField(currentProvider) {
    const secretField = this.secretFieldController()
    if (!secretField) return

    if (currentProvider === this.savedProvider) {
      secretField.reset()
    } else {
      secretField.edit()
    }
  }

  secretFieldController() {
    if (!this.hasApiKeyTarget) return null

    const el = this.apiKeyTarget.querySelector('[data-controller~="secret-field"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "secret-field") : null
  }
}
